import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import './SalonPayments.css';

function SalonPayments() {
  const { salonData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revenue, setRevenue] = useState({
    today: {
      bookings: 0,
      walkIns: 0,
      total: 0
    },
    week: 0,
    month: 0
  });


  const calculateRevenue = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch all completed bookings
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('salonId', '==', salonData.salonId),
        where('serviceStatus', '==', 'completed')
      );
      
      const querySnapshot = await getDocs(bookingsQuery);
      
      // Date calculations
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday
      startOfWeek.setHours(0, 0, 0, 0);
      
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      startOfMonth.setHours(0, 0, 0, 0);
      
      let todayBookings = 0;
      let todayWalkIns = 0;
      let weekRevenue = 0;
      let monthRevenue = 0;
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const price = data.gstEnabled ? (data.grandTotal ?? data.totalPrice ?? 0) : (data.totalPrice || 0);
        const bookingDate = new Date(data.bookingDate);
        const bookingDay = new Date(bookingDate);
        bookingDay.setHours(0, 0, 0, 0);
        
        // Today's revenue
        if (bookingDay.getTime() === today.getTime()) {
          if (data.isWalkIn) {
            todayWalkIns += price;
          } else {
            todayBookings += price;
          }
        }
        
        // Week's revenue
        if (bookingDay >= startOfWeek) {
          weekRevenue += price;
        }
        
        // Month's revenue
        if (bookingDay >= startOfMonth) {
          monthRevenue += price;
        }
      });
      
      setRevenue({
        today: {
          bookings: todayBookings,
          walkIns: todayWalkIns,
          total: todayBookings + todayWalkIns
        },
        week: weekRevenue,
        month: monthRevenue
      });
    } catch (err) {
      setError('Failed to load revenue data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [salonData]);

  useEffect(() => {
    if (salonData?.salonId) {
      calculateRevenue();
    } else if (salonData && !salonData.salonId) {
      setError('Unable to load salon information. Please refresh the page.');
      setLoading(false);
    }
  }, [salonData, calculateRevenue]);

  const handleRefresh = () => {
    calculateRevenue();
  };

  // Show loading if salon data is not yet loaded
  if (!salonData) {
    return (
      <div className="salon-payments-page">
        <div className="loading-container">
          <p>Loading salon information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="salon-payments-page">
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={handleRefresh} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="salon-payments-page">
      <div className="page-header">
        <div className="header-info">
          <h2>Revenue Summary</h2>
          <p>View your earnings from completed services</p>
        </div>
        <button onClick={handleRefresh} className="refresh-btn" disabled={loading}>
          {loading ? 'Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <p>Loading revenue data...</p>
        </div>
      ) : (
        <>
          {/* Today's Revenue */}
          <div className="revenue-section">
            <h3 className="section-title">📅 Today's Revenue</h3>
            <div className="revenue-cards">
              <div className="revenue-card bookings">
                <div className="card-icon">🗓️</div>
                <div className="card-content">
                  <h4>Regular Bookings</h4>
                  <p className="revenue-amount">₹{revenue.today.bookings.toLocaleString()}</p>
                </div>
              </div>

              <div className="revenue-card walkins">
                <div className="card-icon">🚶</div>
                <div className="card-content">
                  <h4>Walk-in Customers</h4>
                  <p className="revenue-amount">₹{revenue.today.walkIns.toLocaleString()}</p>
                </div>
              </div>

              <div className="revenue-card total-today">
                <div className="card-icon">💰</div>
                <div className="card-content">
                  <h4>Total Today</h4>
                  <p className="revenue-amount">₹{revenue.today.total.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* This Week's Revenue */}
          <div className="revenue-section">
            <h3 className="section-title">📊 This Week's Revenue</h3>
            <div className="revenue-cards">
              <div className="revenue-card week">
                <div className="card-icon">📈</div>
                <div className="card-content">
                  <h4>Total This Week</h4>
                  <p className="revenue-amount large">₹{revenue.week.toLocaleString()}</p>
                  <p className="revenue-note">Sunday to Today</p>
                </div>
              </div>
            </div>
          </div>

          {/* This Month's Revenue */}
          <div className="revenue-section">
            <h3 className="section-title">📆 This Month's Revenue</h3>
            <div className="revenue-cards">
              <div className="revenue-card month">
                <div className="card-icon">🎯</div>
                <div className="card-content">
                  <h4>Total This Month</h4>
                  <p className="revenue-amount large">₹{revenue.month.toLocaleString()}</p>
                  <p className="revenue-note">{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SalonPayments;

